import type { CmsTranslations } from "./locales";

/**
 * 日本語 — FireCMS chrome only.
 *
 * Same scope and the same fallback behaviour as `zh-TW.ts`: the keys an editor
 * actually meets, with everything else falling back to English per key. Keep
 * the two files key-for-key identical — `CmsTranslations` requires it, so
 * adding a string to one and not the other is a compile error rather than a
 * locale that silently drops back to English.
 */
export const ja: CmsTranslations = {
    // Entity form actions
    save: "保存",
    create: "作成",
    create_copy: "複製を作成",
    save_and_close: "保存して閉じる",
    create_copy_and_close: "複製を作成して閉じる",
    create_and_close: "作成して閉じる",
    discard: "破棄",
    clear: "クリア",
    cancel: "キャンセル",
    edit: "編集",
    copy: "コピー",
    delete: "削除",
    close: "閉じる",
    done: "完了",
    ok: "OK",
    add: "追加",
    remove: "削除",
    update: "更新",
    back: "戻る",
    next: "次へ",
    retry: "再試行",
    copy_id: "ID をコピー",
    copied: "コピーしました",

    // Confirmation dialogs. `{{entityName}}` / `{{collectionName}}` are
    // i18next interpolations and must survive translation.
    delete_confirmation_title: "削除しますか？",
    delete_confirmation_body: "このデータを削除します。よろしいですか？",
    delete_multiple_confirmation_body:
        "選択したデータを削除します。よろしいですか？",
    delete_entity_confirm_title: "この{{entityName}}を削除しますか？",
    unsaved_changes_title: "未保存の変更",
    unsaved_changes_body: "未保存の変更があります。破棄しますか？",
    discard_changes: "変更を破棄",
    keep_editing: "編集を続ける",
    are_you_sure_leave: "このページを離れてもよろしいですか？",
    unsaved_changes: "この{{collectionName}}に未保存の変更があります。",
    unsaved_changes_in_entity: "このデータに未保存の変更があります。",
    delete_selected: "削除",
    cannot_delete_selected: "選択したデータの中に削除できないものがあります",

    // Collection table
    search: "検索",
    find_by_id: "ID で検索",
    find_entity_by_id: "ID でデータを検索",
    filter: "絞り込み",
    filters: "絞り込み条件",
    clear_filter: "絞り込みを解除",
    clear_filter_sort: "絞り込み／並び順を解除",
    clear_sort: "並び順を解除",
    no_items: "データがありません",
    no_entries_found: "データが見つかりません",
    all_entries_loaded: "全 {{count}} 件を読み込みました",
    create_your_first_entry: "最初のデータを作成",
    no_results_filter_sort: "現在の絞り込み／並び順では結果がありません",
    no_results: "現在の絞り込み／並び順では結果がありません",
    refresh_data: "再読み込み",
    so_empty: "まだデータがありません…",
    multiple_entities: "複数のデータ",
    loading: "読み込み中",
    column_cannot_be_edited: "この列は直接編集できません",

    // Errors and validation
    required: "必須",
    this_form_has_errors: "このフォームにエラーがあります",
    error: "エラー",
    error_saving_data: "データの保存中にエラーが発生しました",
    saving_data: "データを保存しています",
    entities_saved: "件のデータを保存しました",
    internal_error: "内部エラー",
    this_entity_not_exist: "このデータはデータベースに存在しません",
    entity_not_found: "データが見つかりません",
    error_deleting: "削除中にエラーが発生しました",
    error_before_delete: "削除前にエラーが発生しました",
    this_form_has_been_modified: "このフォームは変更されています",
    value_updated_elsewhere: "この値は別の場所で更新されました",

    // Data types, shown in table headers and property previews
    type: "種類",
    string: "テキスト",
    number: "数値",
    boolean: "はい／いいえ",
    date: "日付",
    map: "グループ",
    array: "リスト",

    // The tag reference picker on the news collection
    reference_not_set: "未選択",
    reference_does_not_exist: "参照先のデータが存在しません",
    value_is_not_reference: "この値は参照ではありません。",
    click_to_edit: "クリックして編集",
    data_is_not_array_of_references: "データが参照のリストではありません",
    collection_does_not_exist:
        "指定されたコレクションが存在しません。コンソールを確認してください",
    add_specific: "{{name}}を追加",
    select_specific: "{{name}}を選択",
    select_from: "{{name}}から選択",
    add_entry: "追加",
    add_on_top: "先頭に追加",
    add_below: "下に追加",
    add_to_field: "{{fieldName}}に追加",

    // Navigation and chrome
    open_menu: "メニューを開く",
    close_drawer: "サイドバーを閉じる",
    navigation_drawer: "ナビゲーション",
    home: "ホーム",
    log_out: "ログアウト",
    search_collections: "コレクションを検索",
    page_not_found: "ページが見つかりません",
    page_not_found_body:
        "このページは存在しないか、アクセス権限がありません",
    back_to_home: "ホームに戻る",
    views_group: "ビュー",
    no_collections_found: "コレクションが見つかりません",

    // Theme
    dark_mode: "ダーク",
    light_mode: "ライト",
    system_mode: "システム",

    // Misc
    copy_url_to_clipboard: "URL をコピー",
    open_image_in_new_tab: "画像を新しいタブで開く",
    id: "ID",
    created_on: "作成日",
    email: "メールアドレス",
    yes: "はい",
    no: "いいえ",
    all: "すべて",
};
